import { AlertTriangle, Building2, ChevronDown, ChevronUp, Link, Search, User } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import BONavigation from "../../../components/admin/BONavigation";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

interface ChainStep {
    siren: string;
    denomination: string;
}

interface UltimateOwner {
    firstName: string;
    lastName: string;
    fullName: string;
    dateNaissance?: string;
}

interface LinkedCompany {
    siren: string;
    denomination: string;
    etatAdministratif: string;
    activitePrincipale?: string;
    adresse?: string;
}

interface FetchError {
    siren: string;
    denomination: string;
    reason: string;
}

interface LinkedNetworkResult {
    message: string;
    rootSiren: string;
    chain: ChainStep[];
    ultimateOwner: UltimateOwner | null;
    linkedCompanies: LinkedCompany[];
    eventsCreated: string[];
    eventsSkipped: string[];
    fetchErrors: FetchError[];
    ownerPerson: { id: string; name: string } | null;
}

export default function CompanyOwnershipTree() {
    const [siren, setSiren] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<LinkedNetworkResult | null>(null);
    const [showEvents, setShowEvents] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = siren.trim();
        if (!/^\d{9}$/.test(trimmed)) {
            setError("Le SIREN doit contenir exactement 9 chiffres.");
            return;
        }
        setError(null);
        setResult(null);
        setLoading(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/trigger/new-restaurant/linked-network`,
                { siren: trimmed },
                { headers: authHeaders() }
            );
            setResult(data);
        } catch (err: any) {
            setError(err?.response?.data?.error || "Une erreur est survenue.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-xl font-semibold text-black">Linked Network</h1>
                    <p className="text-xs text-gray-600 mt-0.5">
                        Résoudre le réseau lié d'une société à partir de son SIREN et créer les événements associés
                    </p>
                </div>

                <BONavigation />

                {/* Form */}
                <form onSubmit={handleSubmit} className="mb-6">
                    <div className="bg-white border border-gray-200 rounded-sm p-5">
                        <div className="flex items-end gap-3">
                            <div className="flex-1">
                                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                    SIREN
                                </label>
                                <input
                                    type="text"
                                    value={siren}
                                    onChange={e => setSiren(e.target.value.replace(/\D/g, "").slice(0, 9))}
                                    placeholder="123456789"
                                    className="w-full border border-gray-200 rounded-sm px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-200"
                                    maxLength={9}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex items-center gap-2 px-5 py-2 bg-black text-white text-sm font-medium rounded-sm hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {loading ? (
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <Search className="w-4 h-4" />
                                )}
                                {loading ? "Traitement..." : "Analyser"}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <p className="mt-2 text-xs text-red-600">{error}</p>
                    )}
                </form>

                {/* Results */}
                {result && (
                    <div className="space-y-6 pb-10">
                        {/* Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            {[
                                { label: "Chaîne", value: result.chain.length },
                                { label: "Sociétés liées", value: result.linkedCompanies.length, highlight: true },
                                { label: "Événements créés", value: result.eventsCreated.length, highlight: result.eventsCreated.length > 0 },
                                { label: "Événements existants", value: result.eventsSkipped.length },
                                { label: "Erreurs fetch", value: (result.fetchErrors || []).length, error: (result.fetchErrors || []).length > 0 },
                            ].map(stat => (
                                <div key={stat.label} className={`border rounded-sm p-4 ${
                                    stat.error ? "border-red-200 bg-red-50/40" :
                                    stat.highlight ? "border-green-200 bg-green-50/40" :
                                    "border-gray-200 bg-white"
                                }`}>
                                    <p className={`text-2xl font-semibold ${stat.error ? "text-red-700" : "text-gray-900"}`}>{stat.value}</p>
                                    <p className={`text-xs mt-0.5 ${stat.error ? "text-red-500" : "text-gray-500"}`}>{stat.label}</p>
                                </div>
                            ))}
                        </div>

                        {/* Ultimate Owner */}
                        {result.ultimateOwner && (
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <User className="w-4 h-4 text-gray-500" />
                                    <h2 className="text-sm font-semibold text-gray-900">Propriétaire ultime</h2>
                                </div>
                                <div className="border border-gray-200 rounded-sm p-4 bg-white flex items-start gap-4">
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-gray-900">{result.ultimateOwner.fullName}</p>
                                        {result.ultimateOwner.dateNaissance && (
                                            <p className="text-xs text-gray-500 mt-0.5">Né(e) le {result.ultimateOwner.dateNaissance}</p>
                                        )}
                                    </div>
                                    {result.ownerPerson && (
                                        <span className="text-xs text-gray-400 font-mono">Person #{result.ownerPerson.id}</span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Chain */}
                        {result.chain.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <Link className="w-4 h-4 text-gray-500" />
                                    <h2 className="text-sm font-semibold text-gray-900">Chaîne de détention ({result.chain.length})</h2>
                                </div>
                                <div className="border border-gray-200 rounded-sm overflow-hidden">
                                    <div className="flex items-center px-4 py-3 gap-0 flex-wrap">
                                        {result.chain.map((step, i) => (
                                            <div key={step.siren} className="flex items-center gap-0">
                                                <div className={`px-3 py-1.5 rounded-sm text-xs ${i === 0 ? "bg-black text-white" : "bg-gray-100 text-gray-800"}`}>
                                                    <span className="font-mono">{step.siren}</span>
                                                    {step.denomination && step.denomination !== step.siren && (
                                                        <span className="ml-1.5 text-[10px] opacity-70">{step.denomination}</span>
                                                    )}
                                                </div>
                                                {i < result.chain.length - 1 && (
                                                    <span className="mx-1.5 text-gray-300 text-xs">→</span>
                                                )}
                                            </div>
                                        ))}
                                        {result.ultimateOwner && (
                                            <>
                                                <span className="mx-1.5 text-gray-300 text-xs">→</span>
                                                <div className="px-3 py-1.5 rounded-sm text-xs bg-gray-100 text-gray-700 border border-gray-200">
                                                    <User className="w-3 h-3 inline mr-1" />
                                                    {result.ultimateOwner.fullName}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Linked Companies */}
                        {result.linkedCompanies.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <Building2 className="w-4 h-4 text-gray-500" />
                                    <h2 className="text-sm font-semibold text-gray-900">
                                        Sociétés liées ({result.linkedCompanies.length})
                                    </h2>
                                </div>
                                <div className="border border-gray-200 rounded-sm overflow-hidden">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="bg-gray-50 border-b border-gray-200">
                                                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">SIREN</th>
                                                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Dénomination</th>
                                                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">NAF</th>
                                                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">État</th>
                                                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Adresse</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.linkedCompanies.map((c, i) => (
                                                <tr key={c.siren} className={`border-b border-gray-100 last:border-0 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                                                    <td className="px-4 py-2.5 font-mono text-gray-700">{c.siren}</td>
                                                    <td className="px-4 py-2.5 text-gray-900 font-medium">{c.denomination}</td>
                                                    <td className="px-4 py-2.5 text-gray-500">{c.activitePrincipale || "—"}</td>
                                                    <td className="px-4 py-2.5">
                                                        <span className={`px-1.5 py-0.5 rounded-sm font-medium ${c.etatAdministratif === "A" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                                                            {c.etatAdministratif === "A" ? "Active" : "Fermée"}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2.5 text-gray-500 max-w-xs truncate">{c.adresse || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Fetch Errors */}
                        {(result.fetchErrors || []).length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <AlertTriangle className="w-4 h-4 text-red-500" />
                                    <h2 className="text-sm font-semibold text-red-700">
                                        Erreurs de récupération INSEE ({result.fetchErrors.length})
                                    </h2>
                                </div>
                                <div className="border border-red-200 rounded-sm overflow-hidden">
                                    <div className="bg-red-50 px-4 py-2.5 border-b border-red-200">
                                        <p className="text-xs text-red-600">
                                            Les sociétés ci-dessous n'ont pas pu être récupérées via l'API INSEE. Aucun événement n'a été créé pour ces SIREN.
                                        </p>
                                    </div>
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="bg-red-50/60 border-b border-red-100">
                                                <th className="text-left px-4 py-2.5 font-semibold text-red-700">SIREN</th>
                                                <th className="text-left px-4 py-2.5 font-semibold text-red-700">Dénomination</th>
                                                <th className="text-left px-4 py-2.5 font-semibold text-red-700">Raison</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.fetchErrors.map((fe, i) => (
                                                <tr key={fe.siren} className={`border-b border-red-50 last:border-0 ${i % 2 === 0 ? "bg-white" : "bg-red-50/20"}`}>
                                                    <td className="px-4 py-2.5 font-mono text-red-700">{fe.siren}</td>
                                                    <td className="px-4 py-2.5 text-gray-800 font-medium">{fe.denomination || "—"}</td>
                                                    <td className="px-4 py-2.5 text-red-500">{fe.reason}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Events detail */}
                        {(result.eventsCreated.length > 0 || result.eventsSkipped.length > 0) && (
                            <div>
                                <button
                                    onClick={() => setShowEvents(v => !v)}
                                    className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors mb-2"
                                >
                                    {showEvents ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                    Détail des événements ({result.eventsCreated.length} créés, {result.eventsSkipped.length} existants)
                                </button>
                                {showEvents && (
                                    <div className="grid grid-cols-2 gap-3">
                                        {result.eventsCreated.length > 0 && (
                                            <div className="border border-green-200 rounded-sm overflow-hidden">
                                                <div className="bg-green-50 px-4 py-2 border-b border-green-200">
                                                    <p className="text-[11px] font-semibold text-green-700 uppercase tracking-wide">Créés ({result.eventsCreated.length})</p>
                                                </div>
                                                <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
                                                    {result.eventsCreated.map(id => (
                                                        <div key={id} className="px-4 py-2 text-xs font-mono text-gray-700">{id}</div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {result.eventsSkipped.length > 0 && (
                                            <div className="border border-gray-200 rounded-sm overflow-hidden">
                                                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                                                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Existants ({result.eventsSkipped.length})</p>
                                                </div>
                                                <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
                                                    {result.eventsSkipped.map(id => (
                                                        <div key={id} className="px-4 py-2 text-xs font-mono text-gray-500">{id}</div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
